import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import { modelOptionLabel } from 'src/motornet/common/selectorMeta';
import type { CatalogMotornetAutoOutcome, CatalogRow, CatalogRowMotornet } from 'src/motornet/types';
import SpinnerButton from 'src/components/SpinnerButton/SpinnerButton';
import toaster from 'src/utils/toaster';
import { useFindMotornet, useMotornetMakes, useMotornetModels, useMotornetVersions, useSetMotornet } from './api';
import { chipState, findByName, findOption, matchesManufacturerCode, motornetKind, outcomeLevel } from './helpers';
import { DetailGrid, StatusNote } from './DetailGrid';
import { formatWithSubtitle, Option, portalProps, useSelectLabels } from './selectStyles';
import styles from './VehicleAssociation.module.scss';
import { WizardField, WizardFields } from './WizardFields';

type Props = {
	row: CatalogRow;
	onClose: () => void;
};

const MotornetPanel = ({ row, onClose }: Props) =>
	chipState(row, 'motornet') === 'automatic' && row.motornet ? (
		<MotornetSummary motornet={row.motornet} onClose={onClose} />
	) : (
		<MotornetWizard row={row} onClose={onClose} />
	);

const MotornetSummary = ({ motornet, onClose }: { motornet: CatalogRowMotornet; onClose: () => void }) => {
	const { t } = useTranslation();

	return (
		<div className="p-3">
			<div className={styles.sectionTitle}>Motornet</div>
			<DetailGrid
				items={[
					{
						label: t('vehicle_association_motornet_code', { defaultValue: 'Codice Motornet' }),
						value: motornet.vehicleCode,
					},
					{
						label: t('vehicle_association_units', { defaultValue: 'Quot. / Disp.' }),
						value: `${motornet.quotableUnits} / ${motornet.availableUnits}`,
					},
				]}
			/>
			<StatusNote kind="automatic">
				{t('vehicle_association_automatic_readonly', {
					defaultValue: 'Trovato dal match automatico, non modificabile',
				})}
			</StatusNote>
			<div className={styles.actions}>
				<Button variant="link" onClick={onClose}>
					{t('close', { defaultValue: 'Chiudi' })}
				</Button>
			</div>
		</div>
	);
};

const MotornetWizard = ({ row, onClose }: Props) => {
	const { t } = useTranslation();
	const kind = motornetKind(row.vehicleKind);
	const makes = useMotornetMakes();
	const makeList = (makes.data ?? []).filter((m) => !kind || m.vehicleKind === kind);
	const rowMake = findByName(makeList, row.make, (m) => m.nome);
	const [pickedMake, setPickedMake] = useState<string>();
	const [modelCode, setModelCode] = useState<string>();
	const [vehicleCode, setVehicleCode] = useState<string>();
	const makeCode = rowMake?.acronimo ?? pickedMake;
	const models = useMotornetModels(makeCode, kind);
	const versions = useMotornetVersions(makeCode, modelCode, kind);
	const setCode = useSetMotornet();
	const findCode = useFindMotornet();

	const outcomeMessages: Record<CatalogMotornetAutoOutcome, string> = {
		associated: t('vehicle_association_outcome_associated', { defaultValue: 'Codice Motornet trovato e salvato' }),
		already_associated: t('vehicle_association_outcome_already_associated', {
			defaultValue: 'La riga ha già un codice Motornet',
		}),
		ambiguous: t('vehicle_association_outcome_ambiguous', {
			defaultValue: "Più codici compatibili o codice già usato da un'altra riga: sceglilo a mano",
		}),
		not_found: t('vehicle_association_outcome_not_found', { defaultValue: 'Nessun codice Motornet compatibile' }),
		lookup_failed: t('vehicle_association_outcome_lookup_failed', {
			defaultValue: 'Motornet non ha risposto, riprova',
		}),
		conflict: t('vehicle_association_outcome_conflict', {
			defaultValue: 'Codice trovato, ma una scelta manuale o una riserva impedisce di salvarlo',
		}),
	};

	const handleError = (err: Error) =>
		toaster.error(
			err.message ||
				t('vehicle_association_save_error', { defaultValue: "Errore nel salvataggio dell'associazione" })
		);

	const error = makes.error ?? models.error ?? versions.error;
	const busy = setCode.isPending || findCode.isPending;
	const { noOptions, makeLabel, modelLabel, versionLabel } = useSelectLabels();
	const makeOptions: Option<string>[] = makeList.map((m) => ({ value: m.acronimo, label: m.nome }));
	const modelOptions: Option<string>[] = (models.data ?? [])
		.map((m) => ({ value: m.gammaModello?.codice ?? '', label: modelOptionLabel(m) }))
		.filter((o) => o.value)
		.sort((a, b) => a.label.localeCompare(b.label));
	const versionOptions: Option<string>[] = (versions.data ?? [])
		.filter((v) => v.codiceEurotax)
		.map((v) => ({
			value: v.codiceEurotax,
			label: v.nome || v.versione || v.codiceEurotax,
			subtitle: [v.codiceEurotax, v.codiceCostruttore].filter(Boolean).join(' · '),
			highlight: matchesManufacturerCode(v.codiceCostruttore, row),
		}));

	const save = () => {
		if (!vehicleCode) return;
		setCode.mutate(
			{ versionKey: row.versionKey, snapshot: row.snapshot, vehicleCode },
			{
				onSuccess: () => {
					toaster.success(t('vehicle_association_saved', { defaultValue: 'Associazione salvata' }));
					onClose();
				},
				onError: handleError,
			}
		);
	};

	const findAutomatically = () =>
		findCode.mutate(
			{ versionKey: row.versionKey, snapshot: row.snapshot },
			{
				onSuccess: ({ outcome }) => {
					toaster[outcomeLevel(outcome)](outcomeMessages[outcome]);
					if (outcome === 'associated') onClose();
				},
				onError: handleError,
			}
		);

	return (
		<div className="p-3">
			<div className={styles.sectionTitle}>
				{t('vehicle_association_associate_provider', { provider: 'Motornet', defaultValue: 'Associa {{provider}}' })}
			</div>
			{row.motornet && (
				<div className="mb-3">
					<StatusNote kind="manual">
						{t('vehicle_association_motornet_current_manual', {
							code: row.motornet.vehicleCode,
							defaultValue: 'Codice attuale, scelto a mano: {{code}}',
						})}
					</StatusNote>
				</div>
			)}
			{error && <div className="text-danger mb-2">{error.message || t('error_loading_data', { defaultValue: 'Errore non specificato durante il caricamento dei dati' })}</div>}
			<WizardFields>
				<WizardField label={t('brand', { defaultValue: 'Marca' })} width="calc(100% / 6)">
					{rowMake ? (
						<Form.Control value={rowMake.nome} aria-label={t('brand', { defaultValue: 'Marca' })} readOnly disabled />
					) : (
						<Select<Option<string>>
							options={makeOptions}
							value={findOption(makeOptions, makeCode)}
							isDisabled={busy}
							isLoading={makes.isFetching}
							placeholder={makeLabel}
							aria-label={makeLabel}
							{...portalProps}
							noOptionsMessage={noOptions}
							onChange={(option) => {
								if (!option) return;
								setPickedMake(option.value);
								setModelCode(undefined);
								setVehicleCode(undefined);
							}}
						/>
					)}
				</WizardField>
				<WizardField label={t('model', { defaultValue: 'Modello' })} width={280}>
					<Select<Option<string>>
						options={modelOptions}
						value={findOption(modelOptions, modelCode)}
						isDisabled={busy || !makeCode}
						isLoading={makes.isFetching || models.isFetching}
						placeholder={modelLabel}
						aria-label={modelLabel}
						{...portalProps}
						noOptionsMessage={noOptions}
						onChange={(option) => {
							if (!option) return;
							setModelCode(option.value);
							setVehicleCode(undefined);
						}}
					/>
				</WizardField>
				<WizardField label={t('version', { defaultValue: 'Versione' })} width={420}>
					<Select<Option<string>>
						options={versionOptions}
						formatOptionLabel={formatWithSubtitle}
						value={findOption(versionOptions, vehicleCode)}
						isDisabled={busy || !modelCode}
						isLoading={versions.isFetching}
						placeholder={versionLabel}
						aria-label={versionLabel}
						{...portalProps}
						noOptionsMessage={noOptions}
						onChange={(option) => option && setVehicleCode(option.value)}
					/>
				</WizardField>
			</WizardFields>
			<div className={styles.actions}>
				<Button variant="link" disabled={busy} onClick={onClose}>
					{t('cancel', { defaultValue: 'Annulla' })}
				</Button>
				<Button variant="secondary" disabled={busy} onClick={findAutomatically}>
					<SpinnerButton
						busy={findCode.isPending}
						label={t('vehicle_association_find_auto', { defaultValue: 'Cerca automaticamente' })}
					/>
				</Button>
				<Button
					disabled={busy || !vehicleCode || vehicleCode === row.motornet?.vehicleCode}
					onClick={save}
				>
					<SpinnerButton
						busy={setCode.isPending}
						label={t('vehicle_association_save_link', { defaultValue: 'Salva associazione' })}
					/>
				</Button>
			</div>
		</div>
	);
};

export default MotornetPanel;
